import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRightIcon, XIcon } from 'lucide-react';
import type { SeriesSet } from '../../types/dashboard';
import type { TerritoryRow } from '../../utils/territoryRows';
import { monthLong } from '../../utils/calendar';
import { change, changeKind, formatChange, formatValue, isCountMetric, metricMeta, monthlyBasis, monthValue, type TrendMetric } from '../../utils/metricConfig';
import { DeltaBadge } from '../ui/DeltaBadge';
interface MonthDrawerProps {
  metric: TrendMetric;
  month: number | null;
  onClose: () => void;
  series: SeriesSet;
  network: SeriesSet;
  networkUnits: (m: number) => number;
  scopeUnits: (m: number) => number;
  rows: TerritoryRow[];
  onOpen: (territoryId: string) => void;
}
export function MonthDrawer(props: MonthDrawerProps) {
  const {
    month,
    onClose
  } = props;
  useEffect(() => {
    if (month === null) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [month, onClose]);
  return <AnimatePresence data-id="element-296" data-mp="35b4gc9cb52wv">
      {month !== null && <div key="month-drawer" className="fixed inset-0 z-50" data-id="element-297" data-mp="3k13dc8s4m8ar">
          <motion.div className="absolute inset-0 bg-ink/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} data-id="element-298" data-mp="3f31yy15nax5m" />
          <motion.aside role="dialog" aria-modal="true" aria-label={`${monthLong(month)} detail`} initial={{
        x: 32,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 32,
        opacity: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute right-0 top-0 flex h-full w-full max-w-[460px] flex-col bg-white shadow-2xl" data-id="element-299" data-mp="2rfajwpsq060n">
            <DrawerBody {...props} month={month} data-id="element-300" data-mp="bla0c3x07eyg" />
          </motion.aside>
        </div>}
    </AnimatePresence>;
}
interface Ranked {
  row: TerritoryRow;
  value: number;
  gap: number;
}
function DrawerBody({
  metric,
  month,
  onClose,
  series,
  network,
  networkUnits,
  scopeUnits,
  rows,
  onOpen
}: MonthDrawerProps & {
  month: number;
}) {
  const meta = metricMeta(metric);
  const count = isCountMetric(metric);
  const kind = changeKind(metric);
  const fmt = (v: number | null) => v === null ? '—' : formatValue(metric, v);
  const total = monthValue(metric, series.all, month);
  const netMonth = monthValue(metric, network.all, month);
  const nu = networkUnits(month);
  const bench = count ? netMonth !== null && nu ? netMonth / nu : null : netMonth;
  const ranked: Ranked[] = rows.filter(r => r.territory.launchMonth <= month).map(r => {
    const v = monthValue(metric, r.series.all, month);
    return {
      row: r,
      value: v ?? 0,
      gap: change(metric, v, bench) ?? 0
    };
  }).sort((a, b) => a.gap - b.gap);
  const below = ranked.filter(r => r.gap < 0).slice(0, 5);
  const above = ranked.filter(r => r.gap >= 0).reverse().slice(0, 5);
  const stats = [{
    label: metric === 'members' ? 'New members' : count ? `Total ${meta.label.toLowerCase()}` : meta.label,
    value: fmt(total),
    mom: change(metric, total, monthValue(metric, series.all, month - 1))
  }, {
    label: 'Digital',
    value: fmt(monthValue(metric, series.Digital, month)),
    mom: null
  }, {
    label: 'Local / Other',
    value: fmt(monthValue(metric, series.Local, month)),
    mom: null
  }, {
    label: 'Unknown / Unclassified',
    value: fmt(monthValue(metric, series.Unknown, month)),
    mom: null
  }, {
    label: count ? 'Network avg / territory' : 'Network',
    value: fmt(bench),
    mom: null
  }, {
    label: 'Active territories in scope',
    value: String(scopeUnits(month)),
    mom: null
  }, {
    label: 'YoY (same month)',
    value: (() => {
      const y = change(metric, total, monthValue(metric, series.all, month - 12));
      return y === null ? 'n/a' : formatChange(metric, y);
    })(),
    mom: null
  }];
  const List = ({
    title,
    items
  }: {
    title: string;
    items: Ranked[];
  }) => <div data-id="element-301" data-mp="2xussg1ywvtbc">
      <h3 className="mb-2 text-xs font-semibold text-ink" data-id="element-302" data-mp="2xuj0w9y7xe3j">{title}</h3>
      {items.length === 0 ? <p className="text-xs text-ink-subtle" data-id="element-303" data-mp="2j6eht4v970k8">None in current scope.</p> : <ul className="divide-y divide-line rounded-md border border-line" data-id="element-304" data-mp="2o4zgv0yrre73">
          {items.map(({
        row,
        value,
        gap
      }) => <li key={row.territory.id} data-id="element-305" data-mp="2a4yh5ou8qct7">
              <button type="button" onClick={() => onOpen(row.territory.id)} className="flex w-full items-center gap-3 px-3 py-2 text-left text-xs hover:bg-canvas" data-id="element-306" data-mp="2a3wyay6zgm0x">
                <span className="flex-1 font-medium text-navy" data-id="element-307" data-mp="208273eop1imj">{row.territory.name}</span>
                <span className="tabular-nums text-ink-muted" data-id="element-308" data-mp="255bw4t867s9u">{fmt(value)}</span>
                <span className={`w-16 text-right font-semibold tabular-nums ${gap < 0 ? 'text-negative' : 'text-positive'}`} data-id="element-309" data-mp="3ulbcaua7ihyz">{formatChange(metric, gap)}</span>
                <ChevronRightIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-310" data-mp="3dz06to6kupx" />
              </button>
            </li>)}
        </ul>}
    </div>;
  return <>
      <header className="flex items-start justify-between border-b border-line px-5 py-4" data-id="element-311" data-mp="1iz9eufsh5q2m">
        <div data-id="element-312" data-mp="1xne11tqqkjq5">
          <h2 className="text-lg font-semibold text-navy" data-id="element-313" data-mp="1xo5mwcuj7338">{monthLong(month)} · {meta.label}</h2>
          <p className="text-xs text-ink-muted" data-id="element-314" data-mp="1sub5xh6b927u">{monthlyBasis(metric).replace(/^./, c => c.toUpperCase())} · sample data</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-ink-muted hover:bg-canvas hover:text-ink" data-id="element-315" data-mp="21o7n0hkmboge">
          <XIcon className="h-4 w-4" data-id="element-316" data-mp="2gkf7lizxurw7" />
        </button>
      </header>
      <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5" data-id="element-317" data-mp="2bmn5xx9hrret">
        <div className="grid grid-cols-2 gap-x-4 gap-y-4" data-id="element-318" data-mp="2bkan7hvwtd0p">
          {stats.map(st => <div key={st.label} data-id="element-319" data-mp="m7p9sq6g8yjj">
              <div className="text-[11.5px] text-ink-muted" data-id="element-320" data-mp="h8m7hdhut6f9">{st.label}</div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums text-navy" data-id="element-321" data-mp="hap5okt5u5v2">{st.value}</div>
              {st.mom !== null && <DeltaBadge value={st.mom} kind={kind} label="MoM" data-id="element-322" data-mp="1axtnev5nb4wl" />}
            </div>)}
        </div>
        <List title="Furthest below network" items={below} data-id="element-323" data-mp="1fwn2ut4ht5ze" />
        <List title="Furthest above network" items={above} data-id="element-324" data-mp="1fz9aesrdvlhm" />
      </div>
    </>;
}